import { DownloadOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { http } from '../../api/client.js';
import { tx } from '../../i18n/translate.js';
import { AuditLogDetailDrawer } from './AuditLogDetailDrawer.js';
import type { AuditListResponse, AuditRow } from './audit-log.types.js';

const { RangePicker } = DatePicker;
const PAGE_SIZE = 30;

export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<AuditListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const page = positiveInteger(searchParams.get('page'));
  const filterKey = searchParams.toString();
  const filters = useMemo(() => readFilters(new URLSearchParams(filterKey)), [filterKey]);

  const refresh = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(false);
    http
      .get<AuditListResponse>('/audit-log', { params: { page, pageSize: PAGE_SIZE, ...filters } })
      .then((response) => {
        if (active) setData(response.data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, filters]);

  useEffect(() => refresh(), [refresh]);

  const updateFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const updateRange = (dates: null | [Dayjs | null, Dayjs | null]) => {
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    if (dates?.[0]) next.set('since', dates[0].startOf('day').toISOString());
    else next.delete('since');
    if (dates?.[1]) next.set('until', dates[1].endOf('day').toISOString());
    else next.delete('until');
    setSearchParams(next);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await http.get<Blob>('/audit-log/export.csv', {
        params: filters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `crm-audit-${dayjs().format('YYYYMMDD-HHmmss')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error(tx('hardcoded:pages.admin.auditlogpage.export_failed'));
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<AuditRow> = [
    {
      title: tx('hardcoded:pages.admin.auditlogpage.9438cf9a'),
      dataIndex: 'occurred_at',
      width: 170,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: tx('hardcoded:pages.admin.auditlogpage.c2706623'),
      dataIndex: 'user_name',
      width: 140,
      render: (value: string | null) =>
        value ?? tx('hardcoded:pages.admin.auditlogpage.system_actor'),
    },
    {
      title: tx('hardcoded:pages.admin.auditlogpage.bcc23db9'),
      dataIndex: 'event_type',
      width: 230,
      ellipsis: true,
      render: (value: string) => <Typography.Text code>{value}</Typography.Text>,
    },
    {
      title: tx('hardcoded:pages.admin.auditlogpage.target'),
      width: 220,
      render: (_, row) => (
        <Typography.Text ellipsis={{ tooltip: row.entity_id ?? row.target_ref ?? undefined }}>
          {[row.entity_name, row.entity_id ?? row.target_ref].filter(Boolean).join(' / ') || '—'}
        </Typography.Text>
      ),
    },
    {
      title: tx('hardcoded:pages.admin.auditlogpage.request'),
      width: 260,
      render: (_, row) => (
        <Typography.Text ellipsis={{ tooltip: row.request_path ?? undefined }}>
          {[row.request_method, row.request_path].filter(Boolean).join(' ') || '—'}
        </Typography.Text>
      ),
    },
    {
      title: tx('hardcoded:pages.admin.auditlogpage.7b61a3fd'),
      dataIndex: 'result',
      width: 100,
      render: (value: string) => <Tag color={value === 'success' ? 'green' : 'red'}>{value}</Tag>,
    },
    {
      title: tx('hardcoded:pages.admin.auditlogpage.detail'),
      key: 'detail',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => setSelectedId(String(row.audit_id))}
        >
          {tx('hardcoded:pages.admin.auditlogpage.detail')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {tx('hardcoded:pages.admin.auditlogpage.f2a9b278')}
          </Typography.Title>
        }
        extra={
          <Space wrap>
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={exportCsv}>
              {tx('hardcoded:pages.admin.auditlogpage.export')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              {tx('hardcoded:pages.admin.auditlogpage.9221d699')}
            </Button>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            key={`actor-${filters.actor ?? ''}`}
            allowClear
            placeholder={tx('hardcoded:pages.admin.auditlogpage.actor')}
            defaultValue={filters.actor}
            onSearch={(value) => updateFilter('actor', value.trim() || undefined)}
            style={{ width: 190 }}
          />
          <Input.Search
            key={`entity-${filters.entityName ?? ''}`}
            allowClear
            placeholder={tx('hardcoded:pages.admin.auditlogpage.entity')}
            defaultValue={filters.entityName}
            onSearch={(value) => updateFilter('entityName', value.trim() || undefined)}
            style={{ width: 180 }}
          />
          <Input.Search
            key={`event-${filters.eventType ?? ''}`}
            allowClear
            placeholder={tx('hardcoded:pages.admin.auditlogpage.3df01b3f')}
            defaultValue={filters.eventType}
            onSearch={(value) => updateFilter('eventType', value.trim() || undefined)}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            placeholder={tx('hardcoded:pages.admin.auditlogpage.7b61a3fd')}
            value={filters.result}
            onChange={(value) => updateFilter('result', value)}
            style={{ width: 130 }}
            options={[
              { label: 'success', value: 'success' },
              { label: 'failure', value: 'failure' },
              { label: 'denied', value: 'denied' },
              { label: 'retrying', value: 'retrying' },
            ]}
          />
          <RangePicker
            value={[
              filters.since ? dayjs(filters.since) : null,
              filters.until ? dayjs(filters.until) : null,
            ]}
            onChange={updateRange}
          />
        </Space>
        {error ? (
          <Alert
            type="error"
            showIcon
            message={tx('hardcoded:pages.admin.auditlogpage.load_failed')}
            description={tx('hardcoded:pages.admin.auditlogpage.load_failed_help')}
            action={
              <Button onClick={refresh}>{tx('hardcoded:pages.admin.auditlogpage.retry')}</Button>
            }
          />
        ) : (
          <Table
            rowKey="audit_id"
            loading={loading}
            columns={columns}
            dataSource={data?.rows ?? []}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data?.total ?? 0,
              showTotal: (total) =>
                tx('hardcoded:pages.admin.auditlogpage.41fa603b', { v0: total }),
              onChange: (nextPage) => {
                const next = new URLSearchParams(searchParams);
                next.set('page', String(nextPage));
                setSearchParams(next);
              },
            }}
            scroll={{ x: 1250 }}
          />
        )}
      </Card>
      <AuditLogDetailDrawer auditId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}

function positiveInteger(value: string | null) {
  const parsed = Number(value ?? '1');
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
}

function readFilters(params: URLSearchParams) {
  return {
    actor: params.get('actor') ?? undefined,
    entityName: params.get('entityName') ?? undefined,
    eventType: params.get('eventType') ?? undefined,
    result: params.get('result') ?? undefined,
    since: params.get('since') ?? undefined,
    until: params.get('until') ?? undefined,
  };
}
